/**
 * Events Service
 * Business logic and API calls for events
 */

import { BaseService } from './base.service'
import { 
  Event, 
  CreateEventDTO, 
  UpdateEventDTO,
  SetFeaturedEventDTO,
  ReorderItemDTO,
  FilterParams,
  ID 
} from '../types'

export class EventsService extends BaseService {
  private readonly endpoint = '/api/events-bo'

  /**
   * Get all events with optional filters
   */
  async getAll(params?: FilterParams & {
    category_id?: ID
    upcoming?: boolean
  }): Promise<Event[]> {
    const queryString = params ? this.buildQueryString(params) : ''
    return this.get<Event[]>(`${this.endpoint}${queryString}`)
  }

  /**
   * Get event by ID
   */
  async getById(id: ID): Promise<Event> {
    return this.get<Event>(`${this.endpoint}/${id}`)
  }

  /**
   * Get events by category
   */
  async getByCategory(categoryId: ID): Promise<Event[]> {
    return this.get<Event[]>(`${this.endpoint}/by-category/${categoryId}`)
  }

  /**
   * Get featured events list
   */
  async getFeaturedList(): Promise<Event[]> {
    return this.get<Event[]>(`${this.endpoint}/featured/list`)
  }

  /**
   * Create new event
   */
  async create(data: CreateEventDTO): Promise<Event> {
    return this.post<Event, CreateEventDTO>(this.endpoint, data)
  }

  /**
   * Update event
   */
  async update(id: ID, data: Partial<UpdateEventDTO>): Promise<Event> {
    return this.put<Event, Partial<UpdateEventDTO>>(`${this.endpoint}/${id}`, data)
  }

  /**
   * Delete event
   */
  async delete(id: ID): Promise<void> {
    return this.delete<void>(`${this.endpoint}/${id}`)
  }

  /**
   * Set event featured status
   */
  async setFeatured(id: ID, data: SetFeaturedEventDTO): Promise<Event> {
    return this.patch<Event, SetFeaturedEventDTO>(`${this.endpoint}/${id}/set-featured`, data)
  }

  /**
   * Reorder featured events
   */
  async reorderFeatured(events: ReorderItemDTO[]): Promise<void> {
    return this.patch<void, { events: ReorderItemDTO[] }>(
      `${this.endpoint}/featured/reorder`,
      { events }
    )
  }
}

// Export singleton instance
export const eventsService = new EventsService()

